import { useState } from "react";
import { Navigate } from "react-router";

import { usePagination } from "@vega-api/custom/utils/usePagination";
import { useFeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import {
  UiCapabilityKey,
  useUiCapabilities,
} from "@vega-api/extended/auth/useUiCapabilities";
import { useGetWorkbenchApiKeys } from "@vega-api/generated/api-gateway-workbench-v1/api-gateway-workbench-v1";
import { type ApiGatewayWorkbenchApiKey } from "@vega-api/generated/types/apiGatewayWorkbenchApiKey";
import { type ApiGatewayWorkbenchApiKeySortBy } from "@vega-api/generated/types/apiGatewayWorkbenchApiKeySortBy";
import { type ApiGatewayWorkbenchApiKeyStatus } from "@vega-api/generated/types/apiGatewayWorkbenchApiKeyStatus";
import { type SortDirection } from "@vega-api/generated/types/sortDirection";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { SearchInput } from "@vega-core/components/Form/Controls/Input/SearchInput/SearchInput";
import { Notice } from "@vega-core/components/Notice/Notice";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";
import { VegaModulesPaths } from "@vega-modules/paths";

import { SettingsSectionLayout } from "../../../Settings/components/SettingsSectionLayout";

import { ApiDocsModal } from "./ApiDocsModal";
import { ApiKeysTable, type KeySort } from "./ApiKeysTable";
import { DeactivateAllKeysDialog } from "./DeactivateAllKeysDialog";
import { DeactivateKeyDialog } from "./DeactivateKeyDialog";
import { GenerateKeyDialog } from "./GenerateKeyDialog";
import { RotateKeyDialog } from "./RotateKeyDialog";
import { useApiKeysColumns } from "./useApiKeysColumns";

const ALL_STATUSES = "ALL";

export const ReportingApiKeysPage = () => {
  const { featureFlag } = useFeatureFlag();
  const { capabilities } = useUiCapabilities();

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
  const [sort, setSort] = useState<KeySort | null>(null);
  const generateDialog = useBoolean();
  const docsModal = useBoolean();
  const [keyToRotate, setKeyToRotate] =
    useState<ApiGatewayWorkbenchApiKey | null>(null);
  const [keyToDeactivate, setKeyToDeactivate] =
    useState<ApiGatewayWorkbenchApiKey | null>(null);
  const [userToDeactivateAll, setUserToDeactivateAll] =
    useState<ApiGatewayWorkbenchApiKey | null>(null);

  const { currentPage, setCurrentPage, currentPageSize, setCurrentPageSize } =
    usePaginationState();

  // The access gate below redirects, but hooks must run unconditionally —
  // so instead of an early return the listing query is disabled for gated
  // viewers and never hits the network.
  const hasAccess =
    !!featureFlag["gp_api-gateway_workbench-key-management"] &&
    !!capabilities[UiCapabilityKey.CanActAsVegaAdmin];

  // Search, filtering, sorting and pagination all happen server-side; this
  // page only ever holds the currently displayed page of keys.
  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useGetWorkbenchApiKeys(
    {
      page: currentPage,
      pageSize: currentPageSize,
      ...(debouncedSearch.trim() !== ""
        ? { searchQuery: debouncedSearch.trim() }
        : {}),
      ...(statusFilter !== ALL_STATUSES
        ? { status: statusFilter as ApiGatewayWorkbenchApiKeyStatus }
        : {}),
      ...(sort !== null
        ? {
            sortBy: sort.sortBy as ApiGatewayWorkbenchApiKeySortBy,
            sortDirection: sort.sortDirection as SortDirection,
          }
        : {}),
    },
    {
      query: {
        enabled: hasAccess,
        refetchOnWindowFocus: true,
        // Keep the previous page's rows while params change (search, sort,
        // page): the grid must never flip back to [] mid-interaction.
        placeholderData: (previousData) => previousData,
      },
    },
  );

  const pageKeys = response?.data ?? [];
  const meta = response?.meta;

  // Anchor "now" per mount so status derivation is stable within a paint.
  const [now] = useState(() => new Date());

  const resolveUserName = (key: ApiGatewayWorkbenchApiKey) =>
    key.userName ?? m.settings_reportingApiKeys_unknownUser();

  const { columnDefs, renderColumnToggle } = useApiKeysColumns({
    now,
    onDeactivate: setKeyToDeactivate,
    onDeactivateAll: setUserToDeactivateAll,
    onRotate: setKeyToRotate,
  });

  const { paginationElement } = usePagination({
    meta,
    currentPage,
    setCurrentPage,
    isLoading,
    currentPageSize,
    setCurrentPageSize,
    filterDependencies: [debouncedSearch, statusFilter, sort],
  });

  // Both gates must hold; the route must not be directly navigable otherwise.
  if (!hasAccess) {
    return <Navigate replace to={VegaModulesPaths.Settings} />;
  }

  if (isError) {
    return (
      <SettingsSectionLayout title={m.settings_reportingApiKeys_pageTitle()}>
        <Notice
          content={m.settings_reportingApiKeys_error_description()}
          title={m.settings_reportingApiKeys_error_title()}
          type="warning"
          variant="accent"
        />
      </SettingsSectionLayout>
    );
  }

  const statusOptions: OptionType[] = [
    {
      label: m.common_all(),
      value: ALL_STATUSES,
    },
    {
      label: m.settings_reportingApiKeys_status_active(),
      value: "ACTIVE",
    },
    {
      label: m.settings_reportingApiKeys_status_expired(),
      value: "EXPIRED",
    },
    {
      label: m.settings_reportingApiKeys_status_deactivated(),
      value: "DEACTIVATED",
    },
  ];

  return (
    <SettingsSectionLayout
      description={m.settings_reportingApiKeys_explainer()}
      headerAction={
        <div className="flex items-center gap-2">
          <ThemedButton
            dataTestId="reporting-api-keys-docs-link"
            onClick={docsModal.setTrue}
            variant="secondary"
          >
            {m.settings_reportingApiKeys_docsLink()}
          </ThemedButton>
          <ThemedButton
            dataTestId="reporting-api-keys-generate"
            onClick={generateDialog.setTrue}
          >
            {m.settings_reportingApiKeys_generateKey()}
          </ThemedButton>
        </div>
      }
      title={m.settings_reportingApiKeys_pageTitle()}
    >
      <div className="flex flex-col gap-4">
        <Notice
          content={m.settings_reportingApiKeys_banner_description()}
          title={m.settings_reportingApiKeys_banner_title()}
          type="info"
          variant="accent"
        />
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-96">
            <SearchInput
              background="bg-background/primary"
              compact
              name="reporting-api-keys-search"
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={m.settings_reportingApiKeys_searchPlaceholder()}
              value={searchInput}
              withBorder
            />
          </div>
          <div className="w-40">
            <ThemedDropdown
              isClearable={false}
              name="reporting-api-keys-status-filter"
              onChange={(option) => {
                if (option) setStatusFilter(option.value);
              }}
              options={statusOptions}
              placeholder={m.titles_status()}
              value={statusFilter}
            />
          </div>
          <div className="ml-auto">{renderColumnToggle}</div>
        </div>
        <ApiKeysTable
          columns={columnDefs}
          isLoading={isLoading}
          keys={pageKeys}
          onSortChange={setSort}
          paginationElement={paginationElement}
        />
      </div>
      <GenerateKeyDialog
        onGenerated={refetch}
        open={generateDialog.value}
        setOpen={generateDialog.setValue}
      />
      <ApiDocsModal open={docsModal.value} setOpen={docsModal.setValue} />
      {keyToRotate !== null ? (
        <RotateKeyDialog
          apiKey={keyToRotate}
          onClose={() => setKeyToRotate(null)}
          onRotated={refetch}
        />
      ) : null}
      {keyToDeactivate !== null ? (
        <DeactivateKeyDialog
          apiKey={keyToDeactivate}
          onClose={() => setKeyToDeactivate(null)}
          onDeactivated={refetch}
        />
      ) : null}
      {userToDeactivateAll !== null ? (
        <DeactivateAllKeysDialog
          onClose={() => setUserToDeactivateAll(null)}
          onDeactivated={refetch}
          userId={userToDeactivateAll.userId}
          userName={resolveUserName(userToDeactivateAll)}
        />
      ) : null}
    </SettingsSectionLayout>
  );
};
